/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AuthSettingsModule
 */

import {
    AuthProfileType,
    AviPermissionResource,
    IAuthMappingProfile,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { AuthMappingProfile } from 'object-types';
import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';

import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import {
    AuthMappingProfileModalComponent,
} from 'ng/modules/auth-settings/components/auth-mapping-profile-modal';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { RoleFilterConfigItem } from 'ajs/modules/accounts/factories/role/role-filter.config-item.factory';
import { AuthMappingRuleConfigItem } from './auth-mapping-rule.config-item.factory';

import * as l10n from '../auth-settings.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TAuthMappingProfilePartial = Omit<IAuthMappingProfile, 'mapping_rules' | 'dynamic_role_filters'>;

interface IAuthMappingProfileConfig extends TAuthMappingProfilePartial {
    mapping_rules?: RepeatedMessageItem<AuthMappingRuleConfigItem>;
    dynamic_role_filters?: RepeatedMessageItem<RoleFilterConfigItem>;
}

// Constant for mapping_rules field.
const MAPPING_RULES = 'mapping_rules';

// Constant for dynamic_role_filters field.
const DYNAMIC_ROLE_FILTERS = 'dynamic_role_filters';

/**
 * @desc AuthMappingProfile objectTypeItem.
 *
 * @author Aravindh Nagarajan
 */
export class AuthMappingProfileItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    public static ajsDependencies = [
        'l10nService',
    ];

    /** Overriding getConfig()'s return type */
    public getConfig: () => IAuthMappingProfileConfig;

    private readonly l10nService: L10nService;

    constructor(args: {}) {
        const extendedArgs = {
            objectName: 'authmappingprofile',
            objectType: AuthMappingProfile,
            permissionName: AviPermissionResource.PERMISSION_AUTHMAPPINGPROFILE,
            windowElement: AuthMappingProfileModalComponent,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Getter for mappingRules.
     */
    public get mappingRules(): RepeatedMessageItem<AuthMappingRuleConfigItem> {
        return this.config[MAPPING_RULES];
    }

    /**
     * Getter for type.
     */
    public get type(): AuthProfileType {
        return this.config.type;
    }

    /**
     * Opens Create AuthMappingRule Modal.
     */
    public addAuthMappingRule(isLdapProfile: boolean): void {
        this.addChildMessageItem({
            field: MAPPING_RULES,
            modalBindings: {
                isLdapProfile,
            },
        });
    }

    /**
     * Opens Edit AuthMappingRule Modal.
     */
    public editAuthMappingRule(
        messageItem: AuthMappingRuleConfigItem,
        isLdapProfile: boolean,
    ): void {
        this.editChildMessageItem({
            field: MAPPING_RULES,
            messageItem,
            modalBindings: {
                isLdapProfile,
            },
        });
    }

    /**
     * Getter for dynamicRoleFilters.
     */
    public get dynamicRoleFilters(): RepeatedMessageItem<RoleFilterConfigItem> {
        return this.config[DYNAMIC_ROLE_FILTERS] as RepeatedMessageItem<RoleFilterConfigItem>;
    }

    /**
     * Method to add Dynamic Role Filter.
     */
    public addDynamicRoleFilter(): void {
        this.addChildMessageItem({
            field: DYNAMIC_ROLE_FILTERS,
        });
    }

    /**
     * Method to remove Dynamic Role Filter.
     */
    public removeDynamicRoleFilter(
        labelFilter: RoleFilterConfigItem,
    ): void {
        this.dynamicRoleFilters.removeByMessageItem(labelFilter);
    }

    /**
     * Method to edit Dynamic Role Filter.
     */
    public editDynamicRoleFilter(labelFilter: RoleFilterConfigItem): void {
        this.editChildMessageItem({
            field: DYNAMIC_ROLE_FILTERS,
            messageItem: labelFilter,
        });
    }

    /**
     * @override
     * TODO: Remove this when implementing AV-262803.
     */
    public isEditable(): boolean {
        return this.type !== AuthProfileType.AUTH_PROFILE_CLIENT_CERT && super.isEditable();
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.authMappingProfileModalBreadcrumbTitle);
    }
}
